// src/components/patient/billing/PaymentStatusCard.jsx
//
// Read-only view of the canonical, ledger-derived appointment PAYMENT block
// (`billing_payment` on the appointment detail, `payment` on the invoice detail).
//
//   * every status, amount and label is shown exactly as the server sent it --
//     nothing is computed, summed or inferred here;
//   * the method text comes pre-masked from the server ("Cash",
//     "UPI (ref •••• 6789)"); references are never shown in clear;
//   * patients can only view: there is NO mark-paid, method, amount, reference
//     or reversal control. The only action is the existing online Pay / Retry,
//     offered solely when `allowed_actions.can_retry_online_payment` is true.

import React from "react";

import { formatDateTimeInZone } from "../../../lib/displayTimezone";
import { Pill, money } from "../booking/bookingUi";
import { PAYMENT_TONES } from "./InvoiceView";

function Amount({ label, value, currency, testId }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm font-semibold text-text" data-testid={testId}>{money(value, currency)}</dd>
    </div>
  );
}

function isPositive(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
}

export default function PaymentStatusCard({ payment, timeZone, onRetry = null, retryBusy = false, retryLabel = "Pay online" }) {
  if (!payment) return null;
  const currency = payment.currency;
  const actions = payment.allowed_actions || {};
  const history = Array.isArray(payment.history) ? payment.history : [];
  const canRetry = Boolean(actions.can_retry_online_payment && onRetry);

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-slate-50/60 p-4" data-testid="payment-status-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted">Payment</div>
        <Pill tone={PAYMENT_TONES[payment.payment_status] || "neutral"}>
          <span data-testid="payment-status-label">{payment.payment_status_label || payment.payment_status}</span>
        </Pill>
      </div>

      {payment.reconciliation_message ? (
        <p role="status" className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="payment-reconciliation">
          {payment.reconciliation_message}
        </p>
      ) : null}

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Amount label="Total" value={payment.total_amount} currency={currency} testId="payment-total" />
        <Amount label="Paid" value={payment.amount_paid} currency={currency} testId="payment-paid" />
        {isPositive(payment.amount_refunded) ? (
          <Amount label="Refunded" value={payment.amount_refunded} currency={currency} testId="payment-refunded" />
        ) : (
          <Amount label="Outstanding" value={payment.outstanding_amount} currency={currency} testId="payment-outstanding" />
        )}
      </dl>

      {payment.last_payment_method ? (
        <p className="text-sm text-text" data-testid="payment-method">
          <span className="text-muted">Paid by </span>
          <span className="font-semibold">{payment.last_payment_method}</span>
          {payment.last_paid_at ? <span className="text-muted"> · {formatDateTimeInZone(payment.last_paid_at, timeZone)}</span> : null}
        </p>
      ) : null}

      {payment.has_pending_online_payment && payment.payment_status === "not_paid" ? (
        <p className="text-xs text-muted" data-testid="payment-pending-online">An online payment is still being confirmed by the payment provider.</p>
      ) : null}

      {history.length ? (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Payment history</div>
          <ul className="mt-1 divide-y divide-border rounded-xl border border-border bg-white" data-testid="payment-history">
            {history.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid="payment-history-entry">
                <div className="min-w-0">
                  <div className="font-semibold text-text">{entry.label}</div>
                  <div className="text-xs text-muted">
                    {[entry.method_label, entry.status_label, entry.occurred_at ? formatDateTimeInZone(entry.occurred_at, timeZone) : null].filter(Boolean).join(" · ")}
                  </div>
                  {isPositive(entry.refunded_amount) ? (
                    <div className="text-xs text-muted">Refunded {money(entry.refunded_amount, entry.currency)}</div>
                  ) : null}
                </div>
                <div className="font-semibold text-text">
                  {entry.entry_type === "reversal" ? "−" : ""}
                  {money(entry.amount, entry.currency)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {canRetry ? (
        <button type="button" onClick={onRetry} disabled={retryBusy} aria-busy={retryBusy || undefined}
          className="rounded-2xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid="payment-retry">
          {retryBusy ? "Opening payment…" : retryLabel}
        </button>
      ) : null}
    </div>
  );
}
